import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { ArrowRight, Phone, Award, Activity } from 'lucide-react';

const HeroSection = () => {
  const { t } = useLanguage();

  const scrollToContact = (e) => {
    e.preventDefault();
    document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="home"
      className="relative min-h-[90vh] lg:min-h-screen flex items-center pt-24 pb-12 overflow-hidden bg-[#f8fafc]"
      data-testid="hero-section"
    >
      {/* --- BACKGROUND LAYER: Visible on all devices --- */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-y-0 right-0 w-full lg:w-1/2">
          <img
            src="/background-image-gastrocare.png"
            alt="GastroCare Hospital Exterior"
            className="w-full h-full object-cover object-center lg:object-left"
          />
          {/* Overlay Logic:
              - On Mobile: Darker gradient overlay to make white text readable over the image.
              - On Desktop (lg): Transitions to a clean fade into the left content area.
          */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#f8fafc] via-[#f8fafc]/80 lg:via-transparent to-transparent"></div>
          
          {/* Extra dark overlay for mobile readability */}
          <div className="absolute inset-0 bg-white/40 lg:hidden"></div>
        </div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-4xl">
          {/* Trust Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/90 backdrop-blur-sm shadow-sm border border-slate-200 mb-8 animate-fade-in">
            <span className="flex h-2 w-2 rounded-full bg-emerald-500"></span>
            <span className="text-[#800000] font-semibold text-xs uppercase tracking-wider">
              {t('GastroCare Bhopal')}  
            </span>
          </div>

          {/* Responsive Heading */}
          {/* <h1 className="text-3xl md:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight leading-[1.1] mb-6">
            {t('hero_title')}{' '}
            <span className="text-[#800000] block lg:inline-block">
              {t('hero_subtitle')}
            </span>
          </h1> */}

<div className="flex flex-col items-start w-98 pb-2"> {/* Align left; use items-center to center the whole block */}
  <h1 className="inline-block text-3xl md:text-5xl lg:text-5xl font-extrabold text-slate-900 tracking-tight leading-[1.1] mb-6">
    {/* Title Line */}
    <span className="block">
      {t('hero_title')}
    </span>
    
    {/* Subtitle Line */}
    <span className="text-[#800000] block w-full">
      {t('hero_subtitle')}
    </span>
  </h1>
</div>
          <p className="text-lg md:text-xl text-slate-700 lg:text-slate-600 mb-10 max-w-2xl leading-relaxed font-medium lg:font-normal">
            {t('hero_description')}
          </p>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4 mb-16 mt-8">
            <Button
              onClick={scrollToContact}
              className="bg-[#800000] hover:bg-black text-white h-14 lg:h-16 px-10 rounded-full flex items-center justify-center gap-3 text-lg font-bold transition-all duration-300 shadow-xl"
            >
              {t('hero_cta')}
              <ArrowRight className="w-5 h-5" />
            </Button>

            <a href="tel:+917554055123" className="block">
              <Button
                variant="outline"
                className="border-2 border-slate-200 bg-white/80 backdrop-blur-sm text-slate-900 hover:border-[#800000] hover:text-[#800000] h-14 lg:h-16 px-10 rounded-full flex items-center justify-center gap-3 text-lg font-bold w-full transition-all duration-300"
              >
                <Phone className="w-5 h-5" />
                {t('hero_call')}
              </Button>
            </a>
          </div>

          {/* Stats */}
          <div className="flex flex-wrap items-center gap-x-8 lg:gap-x-12 gap-y-6 py-4">
            <div className="flex items-center gap-4 bg-white/60 p-2 pr-4 rounded-2xl backdrop-blur-sm lg:bg-transparent">
              <div className="p-3 bg-rose-50 rounded-xl text-[#800000]">
                <Activity className="w-6 h-6" />
              </div>
              <div>
                <p className="text-xl lg:text-2xl font-bold text-slate-900">100+</p>
                <p className="text-[10px] lg:text-sm font-medium text-slate-500 uppercase">Advanced Beds</p>
              </div>
            </div>

            <div className="flex items-center gap-4 bg-white/60 p-2 pr-4 rounded-2xl backdrop-blur-sm lg:bg-transparent">
              <div className="p-3 bg-rose-50 rounded-xl text-[#800000]">
                <Award className="w-6 h-6" />
              </div>
              <div>
                <p className="text-xl lg:text-2xl font-bold text-slate-900">15+</p>
                <p className="text-[10px] lg:text-sm font-medium text-slate-500 uppercase">Medical Experts</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Decorative Blur */}
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-maroon-50/50 rounded-full blur-3xl -z-10 -translate-x-1/2 translate-y-1/2"></div>
    </section>
  );
};

export default HeroSection;